/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './MultiSeriesChart';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const MultiSeriesChartManager: ComponentManagerFn<'MultiSeriesChart'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MultiSeriesChart'>(platform, 'MultiSeriesChart'),
    type: 'MultiSeriesChart',
    name: 'Multi Series Chart',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Key Attribute',
        type: 'ComboAttributeChooser',
        helpText: 'It is the primary key field in the current data source.',
      },
      labelAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'The attribute that you would want to display as the label on the X-axis.',
      },
      categoryKeyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Category Key Attribute',
        type: 'ComboAttributeChooser',
        helpText:
          'Select a attribute so that the mathematical functions will be performed on this attribute.',
      },
      categoryAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Category Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'To display all the categories based on the attribute that you provide in a legend at the bottom.',
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText: 'Selected attribute value will be plotted on the chart.',
      },
      functionType: {
        category: 'Data',
        label: 'Value Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
        helpText: 'Select a function to do arithmetic operations on data.',
      },
      chartType: {
        label: 'Chart Type',
        options: toOptions([
          'mscolumn2d',
          'mscolumn3d',
          'msbar2d',
          'msbar3d',
          'msarea',
          'msline',
          'stackedcolumn2d',
          'stackedcolumn3d',
          'stackedarea2d',
          'stackedbar2d',
          'stackedbar3d',
          'marimekko',
        ]),
        type: 'radio',
      },

      caption: {
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a title for the Chart.',
      },
      subCaption: {
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a sub title for the chart.',
      },
      xAxisName: {
        label: 'X-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the X-axis.',
      },
      yAxisName: {
        label: 'Y-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the Y-axis.',
      },
      paletteColors: {
        label: 'Palette Colors',
        type: 'string',
        helpText:
          'The list of colors have to be separated by comma e.g., "#FF0000, #0372AB, #FF5904...". The chart will cycle through the list of specified colors and then render the data plot accordingly.',
        returnType: async () => 'string',
      },
      exportEnabled: {
        label: 'Export Enabled',
        type: 'boolean',
        helpText:
          'Enable this to allow export in png, jpg, pdf, svg or xls formats.',
      },
      exportFileName: {
        label: 'Export File Name',
        property: 'Export File Name',
        type: 'string',
        helpText:
          'Specify the name (excluding the extension) of the output (export) file. default: chart',
        returnType: async () => 'string',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        helpText: 'Provide a number to set height of the chart.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText: 'Provide width for the chart.',
      },
      labelDisplay: {
        label: 'Label Display',
        type: 'radio',
        options: toOptions(['auto', 'wrap', 'stagger', 'rotate']),
      },
      showLabels: {
        label: 'Show Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels of the chart.',
      },
      slantLabels: {
        label: 'Slant Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels in slant manner.',
      },
      showLegend: {
        label: 'Show Legend',
        type: 'boolean',
        helpText: 'Enable this to display legend for the chart.',
      },
      legendPosition: {
        label: 'Legend Position',
        type: 'radio',
        options: toOptions(['Bottom', 'Right']),
      },

      showShadow: {
        label: 'Show Shadow',
        type: 'boolean',
        helpText: 'Enable this to display shadow for the chart.',
      },
      showValues: {
        label: 'Show Values',
        type: 'boolean',
        helpText:
          'Enable this to display values if any for the category fields.',
      },
      placeValuesInside: {
        label: 'Place Values Inside',
        type: 'boolean',
        helpText:
          'Enable this to place values inside the symbols of the chart.',
      },
      rotateValues: {
        label: 'Rotate Values',
        type: 'boolean',
        helpText: 'Enable this to rotate the data of the chart.',
      },
      showPercentValues: {
        label: 'Show Percent Values',
        type: 'boolean',
        helpText:
          'Enable this to show percentage values in labels of the chart.',
      },
      showXAxisLine: {
        label: 'Show X-Axis Line',
        type: 'boolean',
        helpText: 'Enable this to display a line along X-axis.',
      },
      showYAxisLine: {
        label: 'Show Y-Axis Line',
        type: 'boolean',
        helpText: 'Enable this to display a line along Y-axis.',
      },
      use3DLighting: {
        label: 'Use 3D Lighting',
        type: 'boolean',
        helpText: 'Enable this to appear 3D lighting effect for the chart.',
      },
      staggerLines: {
        label: 'Stagger Lines',
        type: 'number',
        helpText:
          'Enter a numeric value above 2 to control how many lines to stagger the label to. By default, all labels are displayed in a single line.',
      },
      labelStep: {
        label: 'Label Step',
        type: 'number',
        helpText:
          'Value to display every n-th label, instead of all labels on the x-axis.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      const chartType = itemData.chartType || '';
      switch (prop) {
        case 'showPercentValues':
          return (
            chartType.indexOf('stackedcolumn2d') !== -1 ||
            chartType.indexOf('stackedcolumn3d') !== -1 ||
            chartType.indexOf('stackedarea2d') !== -1 ||
            chartType.indexOf('stackedbar2d') !== -1 ||
            chartType.indexOf('stackedbar3d') !== -1 ||
            chartType.indexOf('marimekko') !== -1
          );
        case 'exportFileName':
          return itemData.exportEnabled;
        case 'staggerLines':
          return itemData.labelDisplay === 'stagger';

        case 'legendPosition':
          return itemData.showLegend;
        case 'placeValuesInside':
          return (
            itemData.showValues &&
            (chartType.indexOf('mscolumn2d') !== -1 ||
              chartType.indexOf('mscolumn3d') !== -1 ||
              chartType.indexOf('msbar2d') !== -1 ||
              chartType.indexOf('msbar3d') !== -1)
          );
        case 'rotateValues':
          return itemData.showValues;
        case 'slantLabels':
          return itemData.showLabels && itemData.labelDisplay === 'rotate';
        case 'labelDisplay':
        case 'labelStep':
          return itemData.showLabels;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    category: 'Fusion Charts',
    canHaveChildren: () => true,
    appendOverlay: true,
    icon: (item) => {
      const { chartType } = item.props;
      return {
        iconName:
          chartType === 'msbar2d' || chartType === 'msbar3d'
            ? 'chart-bar'
            : chartType === 'msarea'
            ? 'chart-area'
            : chartType === 'msline'
            ? 'chart-line'
            : 'chart-column',
      };
    },
    description:
      'A Multiseries Fusion Chart is a graphical representation of data where multiple datasets are plotted together, using symbols such as bars in column charts or lines in line charts, to compare trends or relationships across categories. It represents tabular numeric data, functions, or qualitative structures, offering comprehensive insights into multiple data series simultaneously.',
  };
};

export default MultiSeriesChartManager;
